import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  COMMON_DEVICE_FIELDS,
  DEVICE_TYPE_CODE_BY_NAME,
  PLANT_MEASUREMENT_CODE_BY_NAME,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_EVENTS_METADATA,
  PLANT_FIELDS_METADATA,
} from '@/features/devices/schemas/devices.metadata.ts';
import { ConditionFormFields } from '@/features/rules-shared/schemas/condition-rules.utils.ts';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { Input } from '@/components/ui/input.tsx';

interface ConditionValueBasedOnParameterProps {
  index: number;
  disabled: boolean;
}

export function ConditionValueBasedOnParameter({
  index,
  disabled,
}: ConditionValueBasedOnParameterProps) {
  const isInitialRender = useRef(true);

  const form = useFormContext<ConditionFormFields>();

  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');
  const watchConditionParameter = Number(form.watch(`conditions.${index}.fact`));

  useEffect(() => {
    // skip initial render to avoid clearing user input during component mount
    // also skip for schedule rules value is auto-set to 'true' by ParameterComboboxSelect
    if (!isInitialRender.current && watchRuleType !== 'schedule') {
      form.setValue(`conditions.${index}.value`, '');
    }

    isInitialRender.current = false;

    return () => {
      isInitialRender.current = true;
    };
  }, [watchConditionParameter]);

  if (watchRuleType === 'schedule') {
    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem className="mb-2">
            <FormLabel className="required">Value</FormLabel>
            <input type="hidden" {...field} />
            <Input value="Triggered" readOnly disabled />
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (watchRuleType === 'device' && watchDeviceType) {
    if (watchConditionParameter === COMMON_DEVICE_FIELDS['Event Status']) {
      let OPTIONS: {
        id: string | number;
        name: string;
      }[] = [];

      if (watchDeviceType) {
        const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[watchDeviceType];
        const eventDeviceMetadata = DEVICE_EVENTS_METADATA[deviceTypeId];

        OPTIONS = Object.values(eventDeviceMetadata.parametersByCode)
          .map((param) => ({
            id: param?.id,
            name: param?.name,
          }))
          .sort((a, b) => a?.name?.localeCompare(b?.name));
      }

      return (
        <FormField
          control={form.control}
          name={`conditions.${index}.value`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">Value</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={disabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select value" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {OPTIONS?.map((op) => (
                    <SelectItem key={String(op.id)} value={String(op.id)}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem className="mb-2">
            <FormLabel className="required">Value</FormLabel>
            <FormControl>
              <Input type="number" placeholder="Enter value" {...field} disabled={disabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (watchRuleType === 'plant') {
    let OPTIONS: {
      id: string | number;
      name: string;
    }[] = [];

    if (watchConditionParameter === PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status']) {
      const parameterId = PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status'];

      OPTIONS = Object.values(PLANT_FIELDS_METADATA[parameterId].parametersByCode)
        .map((param) => ({
          id: param?.id,
          name: param?.name,
        }))
        .sort((a, b) => a?.name?.localeCompare(b?.name));
    }

    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">Value</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {OPTIONS?.map((op) => (
                  <SelectItem key={String(op.id)} value={String(op.id)}>
                    {op.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={form.control}
      name={`conditions.${index}.value`}
      render={({ field }) => (
        <FormItem className="mb-2">
          <FormLabel className="required">Value</FormLabel>
          <FormControl>
            <Input type="number" placeholder="Enter value" {...field} disabled={disabled} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
